import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Api, ApiError, connectWs } from './api';
import type { LoginResponse, MeResponse, OfficeCountsDto, OfficeStateDto, Role, SetupItemDto, SetupResponse, WsEvent } from '../../../shared/types';
import type { Ctx, Notice, Prefs } from './ctx';
import { euro, fmtTime, hm, typeLabel } from './format';
import { Shell, type NavItem } from './Shell';
import { applyTheme, savedTheme, type Theme } from './theme';
import { Login, DEFAULT_SERVER } from './views/Login';
import { ConsultantView } from './views/ConsultantView';
import { AgentView } from './views/AgentView';
import { MailPage } from './views/MailPage';
import { QuotesPage } from './views/QuotesPage';
import { DealsPage } from './views/DealsPage';
import { InvoicesPage } from './views/InvoicesPage';
import { BookingPage } from './views/BookingPage';
import { TicketsPage } from './views/TicketsPage';
import { DocumentsPage } from './views/DocumentsPage';
import { PaymentsPage } from './views/PaymentsPage';
import { CollectionsPage } from './views/CollectionsPage';
import { ContactsPage } from './views/ContactsPage';
import { DeadlinesPage } from './views/DeadlinesPage';
import { RecurringPage } from './views/RecurringPage';
import { AgendaPage } from './views/AgendaPage';
import { CompaniesPage } from './views/CompaniesPage';
import { TodayPage } from './views/TodayPage';
import { OfficePage } from './views/OfficePage';
import { CallsPage } from './views/CallsPage';
import { GlobalTitleTips } from './views/Tip';
import { ApprovalsPage } from './views/ApprovalsPage';
import { OfficeRulesPage } from './views/OfficeRulesPage';
import { OfficeReportPage } from './views/OfficeReportPage';
import { SearchPalette } from './SearchPalette';
import { VisitsPage } from './views/VisitsPage';
import { SecurityPage } from './views/SecurityPage';
import { LicensesHomePage } from './views/LicensesHomePage';
import { DownloadsPage } from './views/DownloadsPage';
import { Nis2LicensesPage } from './views/Nis2LicensesPage';
import { RacingLicensesPage } from './views/RacingLicensesPage';
import { CommissionsPage } from './views/CommissionsPage';
import { ClientView } from './views/ClientView';
import { ClientsPage } from './views/ClientsPage';
import { AgentsPage } from './views/AgentsPage';
import { ReportsPage } from './views/ReportsPage';
import { NoticesPage } from './views/NoticesPage';
import { SettingsPage } from './views/SettingsPage';
import { SetupBanner } from './views/SetupBanner';
import { Lock, type LockReason } from './views/Lock';
import { Offline } from './views/Offline';
import type { Creds } from '../preload/preload';

const MAX_NOTICES = 200;

type Phase =
  | { kind: 'boot' }
  | { kind: 'offline'; creds: Creds; error: string } // server non raggiungibile: si riprova senza perdere l'accesso
  | { kind: 'login'; serverUrl: string; error?: string }
  | { kind: 'app'; api: Api; me: MeResponse };

function loadPrefs(): Prefs {
  try {
    return { desktopNotifications: true, helloLock: false, ...(JSON.parse(localStorage.getItem('tt.prefs') ?? '{}') as Partial<Prefs>) };
  } catch {
    return { desktopNotifications: true, helloLock: false };
  }
}

function noticeFor(ev: WsEvent, role: Role, myId: number): Omit<Notice, 'id' | 'at'> | null {
  if (ev.type === 'session.started') {
    return {
      kind: 'start',
      title: role === 'client' ? 'Il consulente è online per te' : 'Sessione avviata',
      body: `${ev.session.clientName} · ${typeLabel(ev.session.type)} · dalle ${fmtTime(ev.session.startedAt)}`,
    };
  }
  if (ev.type === 'session.stopped') {
    return {
      kind: 'stop',
      title: 'Sessione terminata',
      body: `${ev.session.clientName} · ${hm(ev.session.totalMinutes)} · ${euro(ev.session.amountCents)}`,
    };
  }
  if (ev.type === 'quote.updated') {
    const q = ev.quote;
    if (role === 'client' && q.status === 'sent') {
      return { kind: 'info', title: 'Nuovo preventivo', body: `${q.number} · ${q.subject} · ${euro(q.totalCents)}` };
    }
    // «Pronto da inviare» arriva come app.notice (una volta sola), non a ogni aggiornamento del preventivo
    if (role !== 'client' && (q.status === 'accepted' || q.status === 'rejected')) {
      return {
        kind: 'info',
        title: q.status === 'accepted' ? 'Preventivo accettato' : 'Preventivo rifiutato',
        body: `${q.number} · ${q.recipientName} · ${euro(q.totalCents)}`,
      };
    }
    return null;
  }
  if (ev.type === 'app.notice') {
    return { kind: 'info', title: ev.title, body: ev.body };
  }
  if (ev.type === 'ticket.updated') {
    if (ev.byUserId === myId) return null;
    const t = ev.ticket;
    const title = ev.kind === 'new' ? 'Nuova richiesta di assistenza' : ev.kind === 'reply' ? 'Nuova risposta' : t.status === 'resolved' ? 'Richiesta risolta' : 'Richiesta aggiornata';
    return { kind: 'info', title, body: `${role === 'client' ? '' : t.clientName + ' · '}${t.subject}` };
  }
  if (ev.type === 'booking.updated') {
    if (role !== 'admin') return null;
    const b = ev.booking;
    return {
      kind: 'info',
      title: ev.action === 'created' ? 'Nuova prenotazione' : 'Prenotazione annullata',
      body: `${b.userName} · ${new Date(b.startsAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} ${fmtTime(b.startsAt)}`,
    };
  }
  if (ev.type === 'visit.updated') {
    if (ev.aiStatus === 'ready') return { kind: 'info', title: 'Riassunto pronto', body: ev.title };
    if (ev.aiStatus === 'error') return { kind: 'info', title: 'Visita da rivedere', body: ev.title };
    return null;
  }
  if (ev.type === 'agenda.reminder') {
    const e = ev.event;
    return { kind: 'info', title: 'Promemoria: ' + e.title, body: `Alle ${fmtTime(e.startsAt)}${e.location ? ' · ' + e.location : ''}` };
  }
  if (ev.type === 'note.added' && role !== 'admin') {
    return { kind: 'note', title: 'Nuova nota del consulente', body: ev.note.body };
  }
  return null;
}

/** Nome delle pagine come schede in alto (dentro le voci del menu). */
export const TAB_LABEL: Record<string, string> = {
  dashboard: 'Oggi',
  office: 'Ufficio',
  approvals: 'Da approvare',
  'office-rules': 'Regole',
  calls: 'Chiamate',
  'office-report': 'Pagella',
  companies: 'Aziende',
  sessions: 'Ore di lavoro',
  notices: 'Notifiche',
  agenda: 'Calendario',
  visits: 'Visite',
  booking: 'Prenotazioni',
  deals: 'Percorso',
  clients: 'Clienti',
  contacts: 'Rubrica',
  quotes: 'Preventivi',
  documents: 'Documenti',
  tickets: 'Assistenza',
  security: 'Clienti cybersecurity',
  'licenses-home': 'Tutti',
  licenses: 'SlipAngle',
  'licenses-nis2': 'Sentinel',
  invoices: 'Fatture',
  collections: 'Incassi',
  recurring: 'Ricorrenti',
  reports: 'Report',
  mail: 'Posta',
  deadlines: 'Scadenze',
  downloads: 'Download',
  agents: 'Commerciali',
  commissions: 'Provvigioni',
  settings: 'Impostazioni',
};

/** Voci del menu (consulente e commerciali): poche voci, ognuna con le sue schede in alto. La chiave della voce inizia con «s-». */
export const NAV: Record<Role, NavItem[]> = {
  admin: [
    { key: 's-oggi', color: '#f08c2e', icon: 'clock', label: 'Oggi', pages: ['dashboard', 'sessions', 'notices'], hidden: ['notices'] },
    // La squadra di assistenti (Segreteria, Tecnico, Opportunità): cosa fanno, cosa aspetta il tuo sì, le regole
    { key: 's-agenda', color: '#3b82f6', icon: 'calendar', label: 'Agenda', pages: ['agenda', 'visits', 'booking'] },
    { key: 's-clienti', color: '#8b5cf6', icon: 'building', label: 'Clienti', pages: ['companies', 'deals', 'quotes', 'documents', 'tickets', 'contacts', 'clients'], hidden: ['contacts', 'clients'] },
    { key: 's-soldi', color: '#16a36a', icon: 'euro', label: 'Soldi', pages: ['invoices', 'collections', 'recurring', 'reports'] },
    { key: 's-posta', color: '#e5487a', icon: 'mail', label: 'Posta', pages: ['mail'] },
    { key: 's-altro', color: '#8a94a8', icon: 'dots', label: 'Altro', pages: ['deadlines', 'agents', 'commissions', 'downloads', 'settings'], hidden: ['settings'] },
    // Area riservata a te (amministratore), sotto una linea: l'Ufficio con gli assistenti e il Monitoraggio dei clienti di cybersicurezza
    { key: 's-ufficio', color: '#0d9488', icon: 'users', label: 'Ufficio', pages: ['office', 'approvals', 'office-report', 'office-rules', 'calls'], sep: true },
    // Solo per te (amministratore): tutti i clienti di cybersicurezza, in fondo e separato dal CRM di tutti i giorni
    { key: 's-monitoraggio', color: '#d9632f', icon: 'shield', label: 'Monitoraggio', pages: ['security'] },
    // Licenze: il gioco SlipAngle e il programma Sentinel per i clienti. SlipAngle: chi può scaricarlo e giocare, su quanti PC, fino a quando
    { key: 's-licenze', color: '#e11d48', icon: 'key', label: 'Licenze', pages: ['licenses-home', 'licenses', 'licenses-nis2'] },
  ],
  agent: [
    { key: 's-oggi', color: '#f08c2e', icon: 'clock', label: 'Oggi', pages: ['dashboard', 'sessions', 'notices'], hidden: ['notices'] },
    { key: 's-agenda', color: '#3b82f6', icon: 'calendar', label: 'Agenda', pages: ['agenda', 'visits', 'booking'] },
    { key: 's-clienti', color: '#8b5cf6', icon: 'building', label: 'Clienti', pages: ['companies', 'deals', 'quotes', 'documents', 'tickets', 'contacts'], hidden: ['contacts'] },
    { key: 's-soldi', color: '#16a36a', icon: 'euro', label: 'Soldi', pages: ['invoices', 'collections', 'recurring', 'reports'] },
    { key: 's-posta', color: '#e5487a', icon: 'mail', label: 'Posta', pages: ['mail'] },
    { key: 's-altro', color: '#8a94a8', icon: 'dots', label: 'Altro', pages: ['deadlines', 'commissions', 'downloads', 'settings'], hidden: ['settings'], sep: true },
  ],
  client: [
    { key: 'booking', icon: 'clock', label: 'Prenota', group: 'Bacheca' },
    { key: 'tickets', icon: 'chat', label: 'Assistenza', group: 'Supporto' },
    { key: 'documents', icon: 'folder', label: 'Documenti', group: 'Supporto' },
    { key: 'quotes', icon: 'note', label: 'Preventivi', group: 'Amministrazione' },
    { key: 'invoices', icon: 'euro', label: 'Fatture', group: 'Amministrazione' },
    { key: 'payments', icon: 'wallet', label: 'Pagamenti', group: 'Amministrazione' },
    { key: 'reports', icon: 'list', label: 'Report', group: 'Amministrazione' },
    { key: 'settings', icon: 'gear', label: 'Impostazioni', group: 'Sistema' },
  ],
};

export function App() {
  const [phase, setPhase] = useState<Phase>({ kind: 'boot' });
  const [wsOpen, setWsOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(savedTheme);
  const [event, setEvent] = useState<WsEvent | null>(null);
  const [eventSeq, setEventSeq] = useState(0);
  const [version, setVersion] = useState('');
  const [updateReady, setUpdateReady] = useState<string | null>(null);
  const [page, setPageState] = useState('dashboard');
  const [pageParam, setPageParam] = useState<unknown>(null);
  const [prefs, setPrefsState] = useState<Prefs>(loadPrefs);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [locked, setLocked] = useState(false);
  // Perché compare la richiesta dell'impronta (si spiega sempre all'utente)
  const [lockReason, setLockReason] = useState<LockReason>({ kind: 'avvio' });
  const [seenAt, setSeenAt] = useState<string>('');
  const [searchOpen, setSearchOpen] = useState(false);
  // Ricerca unica: Ctrl+K (o Cmd+K sul Mac) da qualunque pagina
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    window.tt.version().then(setVersion).catch(() => undefined);
    return window.tt.onUpdateDownloaded(setUpdateReady);
  }, []);

  // Nuova versione messa online, oppure server appena riavviato (dopo una messa online): controllo subito.
  // Quando lo scaricamento finisce compare «Riavvia ora» in alto, senza chiudere il programma.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (event?.type === 'app.update') void window.tt.checkUpdates().catch(() => undefined);
  }, [eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (wsOpen && wasOpen.current === false && version) void window.tt.checkUpdates().catch(() => undefined);
    if (wsOpen) wasOpen.current = true;
    else if (wasOpen.current) wasOpen.current = false;
  }, [wsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Indietro e avanti come nel browser (23/10/2026, Marco): tasti laterali del mouse, Alt+frecce, e nel browser
  // anche le sue frecce. Ogni cambio di sezione entra nella cronologia; i dati della sezione restano qui in memoria.
  interface NavEntry { page: string; param: unknown; sub: Record<string, unknown> }
  const navStack = useRef<NavEntry[]>([]);
  const navIndex = useRef(-1);
  const pageRef = useRef('dashboard');
  const [sub, setSubState] = useState<Record<string, unknown>>({});
  // Cambia a ogni clic su una voce o scheda: la pagina riparte dalla sua prima schermata
  const [resetKey, setResetKey] = useState(0);
  const hashFor = (p: string, s: Record<string, unknown>) => (p === 'security' && typeof s.site === 'number' ? `#security/${s.site}` : `#${p}`);
  const pushEntry = useCallback((entry: NavEntry) => {
    navStack.current = [...navStack.current.slice(0, navIndex.current + 1), entry].slice(-100);
    navIndex.current = navStack.current.length - 1;
    try {
      window.history.pushState({ ttNav: navIndex.current }, '', hashFor(entry.page, entry.sub));
    } catch {
      // cronologia non disponibile: si resta comunque dove si è
    }
  }, []);
  const setPage = useCallback((p: string, param?: unknown) => {
    // Stessa pagina (clic sulla voce di menu o sulla scheda in cui sono già): torna alla sua prima schermata
    if (p === pageRef.current) setResetKey((k) => k + 1);
    pageRef.current = p;
    setPageState(p);
    setPageParam(param ?? null);
    setSubState({});
    const cur = navStack.current[navIndex.current];
    if (cur && cur.page === p && cur.param === (param ?? null) && Object.keys(cur.sub).length === 0) return;
    pushEntry({ page: p, param: param ?? null, sub: {} });
  }, [pushEntry]);
  const setSub = useCallback((patch: Record<string, unknown>) => {
    const cur = navStack.current[navIndex.current];
    if (!cur) return;
    const next = { ...cur.sub, ...patch };
    setSubState(next);
    pushEntry({ page: cur.page, param: cur.param, sub: next });
  }, [pushEntry]);

  useEffect(() => window.tt.onOpenOffice?.(() => {
    if (phase.kind === 'app') {
      if (phase.me.user.role === 'admin') setPage('office');
    } else {
      // Dopo l'accesso enter() legge questa destinazione.
      window.location.hash = 'office';
    }
  }), [phase, setPage]);

  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const i = (e.state as { ttNav?: number } | null)?.ttNav;
      if (typeof i !== 'number' || !navStack.current[i]) return;
      navIndex.current = i;
      pageRef.current = navStack.current[i].page;
      setPageState(navStack.current[i].page);
      setPageParam(navStack.current[i].param);
      setSubState(navStack.current[i].sub);
    };
    window.addEventListener('popstate', onPop);
    // Nel programma per Windows i tasti laterali del mouse non muovono la cronologia da soli: li colleghiamo noi.
    // Nel browser lo fa già il browser (altrimenti si tornerebbe indietro due volte).
    const isWeb = document.documentElement.classList.contains('is-web');
    let last = 0;
    const go = (dir: -1 | 1) => {
      if (Date.now() - last < 350) return;
      last = Date.now();
      if (dir < 0) window.history.back();
      else window.history.forward();
    };
    const onMouse = (e: MouseEvent) => {
      if (e.button !== 3 && e.button !== 4) return;
      e.preventDefault();
      if (!isWeb && e.type === 'mouseup') go(e.button === 3 ? -1 : 1);
    };
    const onKey = (e: KeyboardEvent) => {
      if (isWeb || !e.altKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      e.preventDefault();
      go(e.key === 'ArrowLeft' ? -1 : 1);
    };
    const offApp = isWeb ? undefined : window.tt.onNavigate?.((dir) => go(dir === 'back' ? -1 : 1));
    window.addEventListener('mouseup', onMouse);
    window.addEventListener('mousedown', onMouse);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('mouseup', onMouse);
      window.removeEventListener('mousedown', onMouse);
      window.removeEventListener('keydown', onKey);
      offApp?.();
    };
  }, []);

  // Campanella: apre le notifiche; premuta di nuovo torna alla sezione di prima
  const back = useRef<{ page: string; param: unknown }>({ page: 'dashboard', param: null });
  const toggleNotices = useCallback(() => {
    if (page === 'notices') {
      setPage(back.current.page, back.current.param);
    } else {
      back.current = { page, param: pageParam };
      setPage('notices');
    }
  }, [page, pageParam, setPage]);

  const setPrefs = useCallback((p: Prefs) => {
    setPrefsState(p);
    localStorage.setItem('tt.prefs', JSON.stringify(p));
  }, []);

  const enter = useCallback(async (api: Api) => {
    const me = await api.get<MeResponse>('/api/auth/me');
    setPhase({ kind: 'app', api, me });
    // Indirizzo con #pagina (es. …/#office): si apre direttamente quella pagina
    const wanted = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    const securitySite = /^security\/([1-9]\d*)$/.exec(wanted);
    const first = securitySite ? 'security' : wanted && wanted in TAB_LABEL ? wanted : 'dashboard';
    const firstParam = securitySite ? Number(securitySite[1]) : null;
    const firstSub = securitySite ? { site: firstParam } : {};
    pageRef.current = first;
    setPageState(first);
    setPageParam(null);
    setSubState(firstSub);
    navStack.current = [{ page: first, param: null, sub: firstSub }];
    navIndex.current = 0;
    try {
      window.history.replaceState({ ttNav: 0 }, '', securitySite ? `#security/${firstParam}` : `#${first}`);
    } catch {
      // niente cronologia
    }
    window.tt.configureUpdates(api.baseUrl).catch(() => undefined);
  }, []);

  /**
   * Entra con l'accesso già salvato su questo PC.
   * Solo un token rifiutato (401) riporta a email e password: se manca il server si riprova,
   * perché all'accensione del PC l'app parte prima che la rete sia pronta.
   */
  const resume = useCallback(
    async (creds: Creds) => {
      try {
        await enter(new Api(creds.serverUrl, creds.token));
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          await window.tt.setCreds(null);
          setPhase({ kind: 'login', serverUrl: creds.serverUrl, error: 'Sessione scaduta: accedi di nuovo.' });
        } else {
          setPhase({ kind: 'offline', creds, error: (e as Error).message });
        }
      }
    },
    [enter],
  );

  const forgetCreds = useCallback(async (serverUrl: string) => {
    await window.tt.setCreds(null);
    setPhase({ kind: 'login', serverUrl });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const creds = await window.tt.getCreds();
      if (cancelled) return;
      if (!creds) {
        setPhase({ kind: 'login', serverUrl: DEFAULT_SERVER });
        return;
      }
      await resume(creds);
    })();
    return () => {
      cancelled = true;
    };
  }, [resume]);

  // Sblocco con impronta/viso: l'app resta collegata (avvisi e notifiche funzionano sempre), ma l'interfaccia è coperta
  // finché non si verifica l'identità: all'avvio e quando si torna dopo almeno 5 minuti lontano dal programma.
  // (Windows considera «nascosta» la finestra anche solo quando un'altra la copre: chiedere l'impronta a ogni
  // cambio di finestra era troppo, 23/10/2026.)
  useEffect(() => {
    if (!prefs.helloLock) return setLocked(false);
    let alive = true;
    // All'avvio: il programma si è appena aperto, oppure si è appena aggiornato (versione diversa dall'ultima volta)
    Promise.all([window.tt.helloAvailability(), window.tt.version().catch(() => '')])
      .then(([h, v]) => {
        if (!alive || h.status !== 'available') return;
        const last = localStorage.getItem('tt.lastVersion');
        if (v) localStorage.setItem('tt.lastVersion', v);
        setLockReason(last && v && last !== v ? { kind: 'aggiornato', version: v } : { kind: 'avvio' });
        setLocked(true);
      })
      .catch(() => undefined);
    const AWAY_MS = 5 * 60_000;
    let hiddenAt: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const away = () => ({ kind: 'assenza' as const, minutes: hiddenAt === null ? 5 : Math.max(5, Math.round((Date.now() - hiddenAt) / 60_000)) });
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          setLockReason(away());
          setLocked(true);
        }, AWAY_MS);
        return;
      }
      if (timer) clearTimeout(timer);
      timer = null;
      // Al ritorno si scrive quanto si è stati lontani davvero
      if (hiddenAt !== null && Date.now() - hiddenAt >= AWAY_MS) {
        setLockReason(away());
        setLocked(true);
      }
      hiddenAt = null;
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [prefs.helloLock]);

  const api = phase.kind === 'app' ? phase.api : null;
  const me = phase.kind === 'app' ? phase.me : null;

  // Registro notifiche per utente (persistito in locale).
  useEffect(() => {
    if (!me) return;
    try {
      setNotices(JSON.parse(localStorage.getItem(`tt.notices.${me.user.id}`) ?? '[]') as Notice[]);
      setSeenAt(localStorage.getItem(`tt.seen.${me.user.id}`) ?? '');
    } catch {
      setNotices([]);
    }
  }, [me?.user.id]);

  const persistNotices = useCallback(
    (list: Notice[]) => {
      if (me) localStorage.setItem(`tt.notices.${me.user.id}`, JSON.stringify(list));
    },
    [me],
  );

  useEffect(() => {
    if (!api || !me) return;
    const stop = connectWs(
      api,
      (ev) => {
        setEvent(ev);
        setEventSeq((n) => n + 1);
        const n = noticeFor(ev, me.user.role, me.user.id);
        if (n) {
          const notice: Notice = { ...n, id: Date.now() + Math.random(), at: new Date().toISOString() };
          setNotices((list) => {
            const next = [notice, ...list].slice(0, MAX_NOTICES);
            persistNotices(next);
            return next;
          });
          if ((me.user.role !== 'admin' || n.kind === 'info') && prefs.desktopNotifications && n.kind !== 'note') {
            window.tt.notify(n.title, n.body).catch(() => undefined);
          }
        }
      },
      setWsOpen,
    );
    return () => {
      stop();
      setWsOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, me?.user.id, prefs.desktopNotifications]);

  // Ufficio (solo amministratore): quante cose aspettano il sì di Marco (puntino sulla voce del menu)
  const [officeCounts, setOfficeCounts] = useState<OfficeCountsDto | null>(null);
  useEffect(() => {
    if (!api || me?.user.role !== 'admin') return setOfficeCounts(null);
    api
      .get<OfficeStateDto>('/api/office')
      .then((s) => setOfficeCounts(s.counts))
      .catch(() => undefined);
  }, [api, me?.user.role]);
  useEffect(() => {
    if (event?.type === 'office.items') setOfficeCounts(event.counts);
  }, [eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Posta (solo commerciale): ogni minuto controllo i messaggi non letti e avviso se ne e arrivato uno nuovo.
  const [mailUnread, setMailUnread] = useState(0);
  // Cose da sistemare perché l'app funzioni: si ricontrollano a ogni cambio di sezione, al ritorno sulla finestra e ogni 5 minuti
  const [setup, setSetup] = useState<SetupItemDto[]>([]);
  useEffect(() => {
    const api = phase.kind === 'app' ? phase.api : null;
    const role = phase.kind === 'app' ? phase.me.user.role : null;
    if (!api || role === 'client') {
      setSetup([]);
      return;
    }
    let stopped = false;
    const check = () => {
      api
        .get<SetupResponse>('/api/setup')
        .then((r) => {
          if (!stopped) setSetup(r.items);
        })
        .catch(() => undefined);
    };
    check();
    const t = setInterval(check, 5 * 60_000);
    window.addEventListener('focus', check);
    return () => {
      stopped = true;
      clearInterval(t);
      window.removeEventListener('focus', check);
    };
  }, [phase, page]);
  const lastMailUid = useRef<number | null>(null);
  useEffect(() => {
    if (!api || !me || me.user.role === 'client') return;
    let stopped = false;
    const poll = async () => {
      try {
        const s = await api.get<{ unread: number; latest: { uid: number; from: { name: string; address: string }; subject: string } | null }>('/api/mail/summary');
        if (stopped) return;
        setMailUnread(s.unread);
        const uid = s.latest?.uid ?? 0;
        if (lastMailUid.current !== null && uid > lastMailUid.current && s.latest && prefs.desktopNotifications) {
          window.tt.notify(`Nuova mail da ${s.latest.from.name || s.latest.from.address}`, s.latest.subject || '(senza oggetto)').catch(() => undefined);
        }
        lastMailUid.current = Math.max(uid, lastMailUid.current ?? 0);
      } catch {
        // casella non configurata o server di posta non raggiungibile: nessun avviso
      }
    };
    void poll();
    const t = setInterval(poll, 60_000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [api, me?.user.id, prefs.desktopNotifications]);

  // Quando si apre la pagina Notifiche, tutto è "letto".
  useEffect(() => {
    if (page === 'notices' && me && notices.length) {
      const now = new Date().toISOString();
      setSeenAt(now);
      localStorage.setItem(`tt.seen.${me.user.id}`, now);
    }
  }, [page, me, notices.length]);

  const unread = useMemo(() => notices.filter((n) => n.at > seenAt).length, [notices, seenAt]);

  const login = async (serverUrl: string, email: string, password: string, quick: boolean) => {
    const a = new Api(serverUrl, null);
    const r = await a.post<LoginResponse>('/api/auth/login', { email, password, device: 'CRM TecnoSide (Windows)' });
    a.token = r.token;
    await window.tt.setCreds({ serverUrl: a.baseUrl, token: r.token, name: r.user.name, email: r.user.email });
    // Accesso rapido: la prossima volta basta impronta o viso (salvato cifrato, solo su questo PC)
    await window.tt.quickSet(quick ? { serverUrl: a.baseUrl, email: r.user.email, password, name: r.user.name } : null).catch(() => false);
    await enter(a);
  };

  const ssoLogin = async (serverUrl: string, r: LoginResponse) => {
    const a = new Api(serverUrl, r.token);
    await window.tt.setCreds({ serverUrl: a.baseUrl, token: r.token, name: r.user.name, email: r.user.email });
    await enter(a);
  };

  const quickLogin = async () => {
    const r = await window.tt.quickLogin('CRM TecnoSide (Windows)');
    if (!r.ok) throw Object.assign(new Error(r.message), { status: r.status });
    await window.tt.setCreds({ serverUrl: r.serverUrl, token: r.token, name: r.name, email: r.email });
    await enter(new Api(r.serverUrl, r.token));
  };

  const logout = async () => {
    if (phase.kind !== 'app') return;
    const url = phase.api.baseUrl;
    try {
      await phase.api.post('/api/auth/logout');
    } catch {
      // il token potrebbe essere già scaduto: procedo comunque
    }
    await window.tt.setCreds(null);
    window.tt.setTray('idle', 'CRM TecnoSide');
    setPhase({ kind: 'login', serverUrl: url });
  };

  if (phase.kind === 'boot') {
    return <div className="boot">Avvio…</div>;
  }

  if (phase.kind === 'offline') {
    return (
      <Offline
        serverUrl={phase.creds.serverUrl}
        error={phase.error}
        version={version}
        onRetry={() => resume(phase.creds)}
        onUsePassword={() => forgetCreds(phase.creds.serverUrl)}
      />
    );
  }

  if (phase.kind === 'login') {
    return <Login serverUrl={phase.serverUrl} error={phase.error} onLogin={login} onQuickLogin={quickLogin} onSsoLogin={ssoLogin} version={version} />;
  }

  const ctx: Ctx = {
    api: phase.api,
    me: phase.me,
    event,
    eventSeq,
    page,
    pageParam,
    setPage,
    sub,
    setSub,
    notices,
    clearNotices: () => {
      setNotices([]);
      persistNotices([]);
    },
    prefs,
    setPrefs,
    live: wsOpen,
    version,
    logout,
  };

  const role = phase.me.user.role;
  const officeWaiting = officeCounts ? officeCounts.approve + officeCounts.alerts : 0;
  const nav = NAV[role].map((n) =>
    n.key === 'mail' || n.pages?.includes('mail') ? { ...n, ping: mailUnread > 0 } : n.key === 's-ufficio' ? { ...n, ping: officeWaiting > 0 } : n,
  );
  // Voce attiva e sue schede; una voce porta all'ultima scheda aperta (ricordata), altrimenti alla prima
  const current = nav.find((n) => n.pages?.includes(page) || n.key === page) ?? (page === 'dashboard' ? nav.find((n) => n.pages?.includes('dashboard')) : undefined);
  const section = current?.pages ? { label: current.label, color: current.color, tabs: current.pages.filter((k) => !current.hidden?.includes(k) || k === page).map((k) => ({ key: k, label: k === 'booking' && role === 'agent' ? 'Prenota' : k === 'sessions' && role === 'agent' ? 'I miei clienti' : k === 'approvals' && officeWaiting ? `Da approvare · ${officeWaiting}` : k === 'office-report' && officeCounts?.toCheck ? `Pagella · ${officeCounts.toCheck}` : (TAB_LABEL[k] ?? k) })) } : null;
  // Un clic sulla voce porta sempre dove portava la prima volta: la sua prima scheda, dalla prima schermata
  const navigate = (k: string) => {
    const item = nav.find((n) => n.key === k);
    setPage(item?.pages ? item.pages[0] : k);
  };
  if (current?.pages && page !== 'notices') localStorage.setItem(`tt.tab.${phase.me.user.id}.${current.key}`, page);

  let content: React.ReactNode;
  if (page === 'notices') content = <NoticesPage ctx={ctx} />;
  else if (page === 'settings') content = <SettingsPage ctx={ctx} />;
  else if (page === 'mail' && role !== 'client') content = <MailPage ctx={ctx} onUnread={setMailUnread} />;
  else if (page === 'quotes') content = <QuotesPage ctx={ctx} />;
  else if (page === 'invoices') content = <InvoicesPage ctx={ctx} />;
  else if (page === 'deals' && role !== 'client') content = <DealsPage ctx={ctx} />;
  else if (page === 'companies' && role !== 'client') content = <CompaniesPage key={JSON.stringify(pageParam ?? null)} ctx={ctx} />;
  else if (page === 'tickets') content = <TicketsPage ctx={ctx} />;
  else if (page === 'documents') content = <DocumentsPage ctx={ctx} />;
  else if (page === 'payments' && role === 'client') content = <PaymentsPage ctx={ctx} />;
  else if (page === 'collections' && role !== 'client') content = <CollectionsPage ctx={ctx} />;
  else if (page === 'contacts' && role !== 'client') content = <ContactsPage ctx={ctx} />;
  else if (page === 'deadlines' && role !== 'client') content = <DeadlinesPage ctx={ctx} />;
  else if (page === 'downloads' && role !== 'client') content = <DownloadsPage ctx={ctx} />;
  else if (page === 'recurring' && role !== 'client') content = <RecurringPage ctx={ctx} />;
  else if (page === 'booking') content = <BookingPage ctx={ctx} />;
  else if (page === 'agenda' && role !== 'client') content = <AgendaPage ctx={ctx} />;
  else if (page === 'visits' && role !== 'client') content = <VisitsPage key={JSON.stringify(pageParam ?? null)} ctx={ctx} />;
  else if (page === 'commissions' && role !== 'client') content = <CommissionsPage ctx={ctx} />;
  else if (page === 'reports') content = <ReportsPage ctx={ctx} />;
  else if (page === 'clients' && role === 'admin') content = <ClientsPage ctx={ctx} />;
  else if (page === 'agents' && role === 'admin') content = <AgentsPage ctx={ctx} />;
  else if (page === 'security' && role === 'admin') content = <SecurityPage ctx={ctx} />;
  else if (page === 'licenses-home' && role === 'admin') content = <LicensesHomePage ctx={ctx} />;
  else if (page === 'licenses' && role === 'admin') content = <RacingLicensesPage ctx={ctx} />;
  else if (page === 'licenses-nis2' && role === 'admin') content = <Nis2LicensesPage ctx={ctx} />;
  else if (page === 'office' && role === 'admin') content = <OfficePage ctx={ctx} />;
  else if (page === 'calls' && role === 'admin') content = <CallsPage ctx={ctx} />;
  else if (page === 'approvals' && role === 'admin') content = <ApprovalsPage ctx={ctx} />;
  else if (page === 'office-rules' && role === 'admin') content = <OfficeRulesPage ctx={ctx} />;
  else if (page === 'office-report' && role === 'admin') content = <OfficeReportPage ctx={ctx} />;
  else if (page === 'sessions' && role === 'admin') content = <ConsultantView ctx={ctx} />;
  else if (page === 'sessions' && role === 'agent') content = <AgentView ctx={ctx} />;
  else if (role === 'admin' || role === 'agent') content = <TodayPage ctx={ctx} />;
  else content = <ClientView ctx={ctx} />;
  if (setup.length && page !== 'notices') {
    content = (
      <>
        <SetupBanner items={setup} onGo={setPage} api={api!} onSaved={() => window.dispatchEvent(new Event('focus'))} compact={page === 'office'} />
        {content}
      </>
    );
  }

  return (
    <>
      <GlobalTitleTips />
      {locked && (
        <Lock
          name={phase.me.user.name}
          email={phase.me.user.email}
          version={version}
          reason={lockReason}
          onUnlocked={() => setLocked(false)}
          onUsePassword={() => {
            setLocked(false);
            logout();
          }}
        />
      )}
      <Shell
        nav={nav}
        page={page}
        onNavigate={navigate}
        section={section}
        onSearch={role === 'client' ? undefined : () => setSearchOpen(true)}
        onNewLead={role === 'client' ? undefined : () => setPage('deals', { newLead: Date.now() })}
        me={phase.me}
        live={wsOpen}
        version={version}
        updateReady={updateReady}
        onInstallUpdate={() => window.tt.installUpdate()}
        unread={unread}
        onToggleNotices={toggleNotices}
        theme={theme}
        onToggleTheme={() =>
          setTheme((t) => {
            const next = t === 'dark' ? 'light' : 'dark';
            applyTheme(next, true);
            return next;
          })
        }
        onLogout={logout}
      >
        <React.Fragment key={resetKey}>{content}</React.Fragment>
      </Shell>
      {searchOpen && role !== 'client' && (
        <SearchPalette
          ctx={ctx}
          pages={nav.flatMap((n) => (n.pages ?? [n.key]).map((k) => ({ key: k, label: TAB_LABEL[k] ?? n.label, section: n.label })))}
          onClose={() => setSearchOpen(false)}
        />
      )}
    </>
  );
}
